/**
 * The device's copy of the workspace (local first, docs/04-domaenenmodell.md "Synchronisation"):
 * snapshot of its storages, bookings after the cutoff, master data, and the queue of own changes
 * not yet confirmed by the server. The view is always: server state + own pending changes.
 * The same class runs in the app (persisted in IndexedDB) and in the tests' simulated devices.
 */
import { mergeEntity } from "./entities.ts";
import type { Entity, EntityChange } from "./entities.ts";
import { computeState, extendState } from "./ledger.ts";
import type { ArticleInfo, ComputeOptions, Resolution, Snapshot, State } from "./ledger.ts";
import { compareBookings } from "./model.ts";
import type { Booking, CountMode } from "./model.ts";
import type { LoadResponse, Op, OpResult, OpsRequest, SyncResponse } from "./protocol.ts";

export interface ReplicaData {
  lagers: string[];
  snapshot: Snapshot | null;
  snapshotSeq: number;
  seq: number;
  bookings: Booking[];
  entities: Entity[];
  resolutions: Record<string, Resolution>;
  resolutionsTag: string;
  pending: Op[];
  rejected: { op: Op; error: string }[];
  needsLoad: boolean;
}

const entityKey = (e: { type: string; id: string }) => `${e.type}|${e.id}`;

/** How the core reads tracking (article) and count mode (article, storage) from the master data. */
export function optionsFromEntities(entities: Iterable<Entity>): ComputeOptions {
  const articles = new Map<string, ArticleInfo>();
  const countModes = new Map<string, CountMode>();
  const mode = (v: unknown): CountMode | undefined => (v === "lot" || v === "article" ? v : undefined);
  for (const e of entities) {
    if (e.type === "article") {
      const tracking = e.fields.tracking === "lot" ? "lot" : "quantity";
      const countMode = mode(e.fields.countMode);
      articles.set(e.id, countMode ? { tracking, countMode } : { tracking });
    } else if (e.type === "lager") {
      const countMode = mode(e.fields.countMode);
      if (countMode) countModes.set(e.id, countMode);
    }
  }
  return { article: (id) => articles.get(id), countMode: (lager) => countModes.get(lager) };
}

export class Replica {
  lagers: string[];
  snapshot: Snapshot | null = null;
  snapshotSeq = 0;
  seq = 0;
  /** Confirmed bookings (after the cutoff) by id. */
  readonly bookings = new Map<string, Booking>();
  /** Master data as the server merged it. */
  readonly entities = new Map<string, Entity>();
  resolutions: Record<string, Resolution> = {};
  resolutionsTag = "";
  /** Own changes, oldest first, until the server confirms them. */
  pending: Op[] = [];
  /** Changes the server refused (shown to the person, not retried). */
  rejected: { op: Op; error: string }[] = [];
  /** The snapshot is outdated or missing: load before the next sync. */
  needsLoad = true;

  private sortedCache: Booking[] | null = null;
  private stateCache: State | null = null;

  constructor(lagers: string[]) {
    this.lagers = [...lagers];
  }

  private changed() {
    this.sortedCache = null;
    this.stateCache = null;
  }

  // ---- own changes ----

  book(booking: Booking) {
    this.pending.push({ type: "booking", booking });
    this.append([booking]);
  }

  /**
   * New bookings that sort after everything known are appended to the computed state; anything
   * else (a late booking, a cancel) means computing anew on the next read.
   */
  private append(bookings: Booking[]) {
    if (bookings.length === 0) return;
    const sorted = [...bookings].sort(compareBookings);
    if (this.stateCache && this.sortedCache && extendState(this.stateCache, sorted, { ...this.stateOptions(), sorted: true })) {
      this.sortedCache.push(...sorted);
      return;
    }
    this.changed();
  }

  /** Master data change (may change tracking or count mode): computed anew. */
  edit(change: EntityChange) {
    this.pending.push({ type: "entity", change });
    this.changed();
  }

  // ---- from the server ----

  /** First load or after "resnapshot": replaces everything confirmed, keeps the queue. */
  applyLoad(res: LoadResponse) {
    this.snapshot = res.snapshot;
    this.snapshotSeq = res.snapshotSeq;
    this.seq = res.seq;
    this.bookings.clear();
    for (const b of res.bookings) this.bookings.set(b.id, b);
    this.entities.clear();
    for (const e of res.entities) this.entities.set(entityKey(e), e);
    this.resolutions = res.resolutions;
    this.resolutionsTag = res.resolutionsTag;
    this.needsLoad = false;
    this.dropConfirmed();
    this.changed();
  }

  /** The request for the next sync: up to `max` queued changes and what the device knows. */
  request(max = 100): OpsRequest {
    return {
      ops: this.pending.slice(0, max),
      since: this.seq,
      lagers: this.lagers,
      snapshot: this.snapshot ? { cutoff: this.snapshot.cutoff, seq: this.snapshotSeq } : null,
      resolutionsTag: this.resolutionsTag,
    };
  }

  /** The answer to a request: confirms the sent changes and takes what is new. */
  applySync(sent: Op[], res: SyncResponse & { results?: OpResult[] }) {
    if (res.results) {
      const done = new Set<Op>();
      sent.forEach((op, i) => {
        const result = res.results![i];
        if (!result) return;
        done.add(op);
        if (!result.ok) this.rejected.push({ op, error: result.error ?? "abgelehnt" });
      });
      this.pending = this.pending.filter((op) => !done.has(op));
    }
    // A late answer (an older number) never overwrites a newer state.
    let reset = Boolean(res.resolutions) || res.entities.length > 0;
    const fresh: Booking[] = [];
    if (res.seq >= this.seq) {
      const known = new Map(this.journal().map((b) => [b.id, b.at]));
      for (const b of res.bookings) {
        const at = known.get(b.id);
        // The server may have capped the time of an own booking: then compute anew.
        if (at === undefined) fresh.push(b);
        else if (at !== b.at) reset = true;
        this.bookings.set(b.id, b);
      }
      for (const e of res.entities) this.entities.set(entityKey(e), e);
      this.seq = res.seq;
    }
    if (res.resolutions) {
      this.resolutions = res.resolutions;
      this.resolutionsTag = res.resolutionsTag;
    }
    if (res.resnapshot) this.needsLoad = true;
    this.dropConfirmed();
    if (reset) this.changed();
    else this.append(fresh);
  }

  /** Own bookings the server already has (e.g. the answer was lost but a sync brought them). */
  private dropConfirmed() {
    this.pending = this.pending.filter((op) => op.type !== "booking" || !this.bookings.has(op.booking.id));
  }

  // ---- the view ----

  /** Master data with own pending changes on top. */
  entityView(): Map<string, Entity> {
    const out = new Map(this.entities);
    for (const op of this.pending) {
      if (op.type !== "entity") continue;
      const key = entityKey(op.change);
      out.set(key, mergeEntity(out.get(key), op.change));
    }
    return out;
  }

  /** All bookings the device knows (confirmed and pending) in canonical order. */
  journal(): Booking[] {
    if (!this.sortedCache) {
      const all = new Map(this.bookings);
      for (const op of this.pending) if (op.type === "booking") all.set(op.booking.id, op.booking);
      this.sortedCache = [...all.values()].sort(compareBookings);
    }
    return this.sortedCache;
  }

  computeOptions(): ComputeOptions {
    return optionsFromEntities(this.entityView().values());
  }

  private stateOptions(): ComputeOptions {
    return {
      ...this.computeOptions(),
      scope: new Set(this.lagers),
      resolutions: new Map(Object.entries(this.resolutions)),
      ...(this.snapshot ? { from: this.snapshot } : {}),
    };
  }

  /** Stock of the device's storages: snapshot + bookings after it + own pending ones. */
  state(): State {
    if (!this.stateCache) this.stateCache = computeState(this.journal(), { ...this.stateOptions(), sorted: true });
    return this.stateCache;
  }

  // ---- persistence ----

  toJSON(): ReplicaData {
    return {
      lagers: this.lagers,
      snapshot: this.snapshot,
      snapshotSeq: this.snapshotSeq,
      seq: this.seq,
      bookings: [...this.bookings.values()],
      entities: [...this.entities.values()],
      resolutions: this.resolutions,
      resolutionsTag: this.resolutionsTag,
      pending: this.pending,
      rejected: this.rejected,
      needsLoad: this.needsLoad,
    };
  }

  static fromJSON(data: ReplicaData): Replica {
    const r = new Replica(data.lagers);
    r.snapshot = data.snapshot;
    r.snapshotSeq = data.snapshotSeq;
    r.seq = data.seq;
    for (const b of data.bookings) r.bookings.set(b.id, b);
    for (const e of data.entities) r.entities.set(entityKey(e), e);
    r.resolutions = data.resolutions;
    r.resolutionsTag = data.resolutionsTag;
    r.pending = data.pending;
    r.rejected = data.rejected;
    r.needsLoad = data.needsLoad;
    return r;
  }
}
